/**
 * Vet signoff block — vet name (via selector or fill field), with optional
 * license number, signature, and date lines.
 * Exports both a react-pdf version (for PDF generation) and an HTML version (for screen preview).
 *
 * The preview embeds the staff list query and auto-populates the license number
 * when a vet is selected from the dropdown, labelling it for the vet's region
 * (RCVS in the UK, state license in the US). The PDF version defaults to the UK
 * "RCVS number" label — its callers are UK-format certificate/consent documents —
 * but accepts a `registrationLabel` override for region-specific variants.
 */
import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { useEffect } from 'react'

import type { StaffMember } from '@snootsvet/shared'
import { getStaffRegistrationLabel } from '@snootsvet/shared'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import {
  FillField,
  Spacer,
  WithInlineResponse,
} from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { mm } from '@/frontend/features/printing/pdf-utils'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { useListStaff } from '@/frontend/hooks/api/use-list-staff'

export interface VetSignoffValues {
  vetName: string
  licenseNumber?: string
}

interface VetSignoffPreviewProps {
  vetName: string
  /** Called when the vet selection changes. Includes the license number if available. */
  onChange: (values: VetSignoffValues) => void
  /** Label before the vet name. Defaults to "Attending veterinary surgeon". */
  label?: string
  /** Whether to show the RCVS / license number field. */
  showLicenseNumber?: boolean
  /**
   * Override for the license number line label. When omitted, the label is
   * derived from the selected vet's region (RCVS in the UK, state license in
   * the US). Pass this to match a fixed label used by the corresponding PDF.
   */
  registrationLabel?: string
  /** Whether to show a signature line. */
  showSignature?: boolean
  /** Whether to show a date line. */
  showDate?: boolean
}

interface VetSignoffPdfProps {
  vetName: string
  label?: string
  licenseNumber?: string
  /** Label for the license number line. Defaults to the UK "RCVS number". */
  registrationLabel?: string
  showSignature?: boolean
  showDate?: boolean
}

/** Format a staff member's full name. */
function staffFullName(s: StaffMember): string {
  return `${s.firstName} ${s.lastName}`
}

const styles = StyleSheet.create({
  bold: { fontFamily: 'Helvetica-Bold' },
})

export function VetSignoffPreview({
  vetName,
  onChange,
  label = 'Attending veterinary surgeon',
  showLicenseNumber = false,
  registrationLabel,
  showSignature = false,
  showDate = false,
}: VetSignoffPreviewProps) {
  const { data: staffList } = useListStaff()
  const { data: currentStaff } = useGetAuthenticatedStaff()

  // Auto-default to the current staff member if they're a vet and no vet is selected yet.
  useEffect(() => {
    if (vetName || !currentStaff) return
    if (currentStaff.role !== 'vet' && currentStaff.role !== 'remote_vet') return
    onChange({
      vetName: `${currentStaff.firstName} ${currentStaff.lastName}`,
      licenseNumber: currentStaff.licenseNumber ?? undefined,
    })
  }, [vetName, currentStaff, onChange])

  const items = (staffList ?? []).map((s) => ({
    value: staffFullName(s),
    label: staffFullName(s),
  }))

  // The license number line is auto-populated from the selected vet (not editable).
  // Its label is the caller's override if given, else derived from the vet's
  // region (RCVS in the UK, state license in the US).
  const selectedStaff = (staffList ?? []).find((s) => staffFullName(s) === vetName)
  const resolvedRegistrationLabel =
    registrationLabel ??
    (selectedStaff ? getStaffRegistrationLabel(selectedStaff.regions) : 'RCVS number')

  function handleSelect(name: string | null) {
    const match = (staffList ?? []).find((s) => staffFullName(s) === name)
    onChange({
      vetName: name ?? '',
      licenseNumber: match?.licenseNumber ?? undefined,
    })
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center gap-2">
        <strong>{label}:</strong>
        <Select value={vetName || null} onValueChange={handleSelect} items={items}>
          <SelectTrigger className="inline-flex w-48">
            <SelectValue placeholder="Select vet…" />
          </SelectTrigger>
          <SelectContent>
            {items.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {showLicenseNumber && (
        <p>
          <strong>{resolvedRegistrationLabel}:</strong>{' '}
          {selectedStaff?.licenseNumber ?? '____________________________'}
        </p>
      )}
      {showSignature && (
        <p>
          <strong>Signature:</strong> ____________________________
        </p>
      )}
      {showDate && (
        <p>
          <strong>Date:</strong> ____________________________
        </p>
      )}
    </div>
  )
}

export function VetSignoff({
  vetName,
  label = 'Attending veterinary surgeon',
  licenseNumber,
  registrationLabel = 'RCVS number',
  showSignature = false,
  showDate = false,
}: VetSignoffPdfProps) {
  return (
    <View wrap={false} style={{ marginTop: mm(6) }}>
      <WithInlineResponse response={<FillField value={vetName || undefined} />}>
        <Text style={styles.bold}>{label}:</Text>
      </WithInlineResponse>
      {licenseNumber != null && (
        <>
          <Spacer height={mm(3)} />
          <WithInlineResponse response={<FillField value={licenseNumber || undefined} />}>
            <Text style={styles.bold}>{registrationLabel}:</Text>
          </WithInlineResponse>
        </>
      )}
      {showSignature && (
        <>
          <Spacer height={mm(6)} />
          <WithInlineResponse response={<FillField />}>
            <Text style={styles.bold}>Signature:</Text>
          </WithInlineResponse>
        </>
      )}
      {showDate && (
        <>
          <Spacer height={mm(4)} />
          <WithInlineResponse response={<FillField />}>
            <Text style={styles.bold}>Date:</Text>
          </WithInlineResponse>
        </>
      )}
    </View>
  )
}
